import React, { useState } from 'react';
import Localize from 'react-intl-universal';
import { useLocation, useNavigate } from 'react-router-dom';

import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import MenuIcon from '@mui/icons-material/Menu';
import { alpha } from '@mui/material';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import Link from '@mui/material/Link';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { styled } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import { isFraunhofer } from '@common/Util';
import { useGlobalContext } from '@hooks/Context/GlobalContext';
import * as routerConstants from '@navigation/Constants.js';

import HandleAvatar from './HandleAvatar';
import LogoLink from './LogoLink';

const drawerWidth = 240;

const StyledLogoBox = styled(Box)(() => ({
  display: 'flex',
  minWidth: '9rem',
  marginRight: '0.5rem',
  alignItems: 'center'
}));

const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'disableComponent'
})(({ disableComponent, theme }) => ({
  backgroundColor: theme.header.backgroundColor,
  boxShadow: 'none',
  pointerEvents: disableComponent ? 'none' : 'auto',
  filter: disableComponent ? 'blur(1.5px)' : 'none',
  marginBottom: disableComponent ? '2rem' : 'none',
  [theme.breakpoints.up('md')]: {
    height: '2.92rem'
  }
}));

const StyledLink = styled(Link)(({ theme }) => ({
  color: theme.header.color
}));

const StyledBrightness = styled(Brightness4Icon)(({ theme }) => ({
  width: '1.2em',
  height: '1.2em',
  color: theme.palette.common.white
}));

const StyledBrightnessDark = styled(Brightness7Icon)(({ theme }) => ({
  width: '1.2em',
  height: '1.2em',
  color: theme.palette.common.white
}));

const StyledTypographyButton = styled(Typography)(({ theme }) => ({
  lineHeight: 4,
  textTransform: 'uppercase',
  backgroundColor: 'transparent',
  border: 0,
  color: theme.palette.common.white,
  paddingLeft: 0
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  display: { xs: 'block', sm: 'none' },
  '& .MuiDrawer-paper': {
    boxSizing: 'border-box',
    width: drawerWidth,
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.dark
  }
}));

const StyledTypography = styled(Typography)(({ theme }) => ({
  fontSize: '11px',
  fontWeight: 'bold',
  lineHeight: '3rem',
  paddingLeft: 0,
    minWidth: 0,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  [theme.breakpoints.up('md')]: {
    fontSize: '11px',
    paddingRight: '10px'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '12px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '13px'
  }
}));

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active' // Prevent `active` from being forwarded to the DOM
})(({ active, capital, hidden, theme }) => ({
  paddingTop: '0.5rem',
  paddingBottom: '0.5rem',
  lineHeight: '1.9',
  borderRadius: 0,
  color: theme.header.color,
  backgroundColor: active ? `${alpha(theme.palette.primary.light, 0.4)}` : 'transparent',
  display: hidden ? 'none' : 'block',
  fontSize: '14px',
  textTransform: capital === 'true' ? 'uppercase' : 'none',
  '&:hover': {
    backgroundColor: theme.palette.primary.light
  }
}));

const Header = ({ config: { header, global }, headerConfig, disableComponent = false }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contextData, , toggleColorMode] = useGlobalContext();
  const { hasHomePage, publicAppBar, privateAppBar } = headerConfig;

  const navigate = useNavigate();
  const location = useLocation();
  const SYS_NAME = import.meta.env.VITE_SYSTEM_NAME;
  const isLogin = !!contextData?.user;
  const menuItems = isLogin
    ? privateAppBar?.items
    : publicAppBar?.items.filter(
        (item) =>
          (hasHomePage || item.name !== 'home') &&
          (contextData?.isCatalogVisible || item.name !== 'eventCatalog')
      );

  const capitalLetters = isLogin ? publicAppBar?.capitalLetters : privateAppBar?.capitalLetters;

  const handleDrawerClose = () => {
    setMobileOpen(false);
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCloseNavMenu = ({ target: { name } }) => {
    navigate(`/${name}`, {
      state: {
        from: location?.state?.from ? location.state.from : '',
        path: location?.state?.path ? location.state.path : ''
      }
    });
  };

  const handleColorMode = () => {
    toggleColorMode();
  };

  const LogoLinkComponent = (
    <StyledLogoBox>
      <LogoLink
        disabled={isFraunhofer()}
        pathname={
          SYS_NAME === '' && headerConfig?.hasHomePage
            ? routerConstants.routes.path.home
            : routerConstants.routes.path.login
        }
        hasHomePage={headerConfig?.hasHomePage}
      />
    </StyledLogoBox>
  );

  const colorMode = (
    <Tooltip
      title={
        contextData.colorMode === 'light'
          ? Localize.get('AppNavBar.darkModeOn')
          : Localize.get('AppNavBar.darkModeOff')
      }
    >
      <IconButton
        sx={{ mr: global?.loginAreaVisibility ? 0 : { sm: 0, md: 4 } }}
        onClick={handleColorMode}
      >
        {contextData?.colorMode === 'light' ? <StyledBrightness /> : <StyledBrightnessDark />}
      </IconButton>
    </Tooltip>
  );

  return (
    <StyledAppBar position='relative' disableComponent={disableComponent}>
      <Container sx={{ display: { xs: 'none', md: 'flex', height: '100% !important' } }}>
        {LogoLinkComponent}
        {SYS_NAME !== '' && <StyledTypography>{`${SYS_NAME} System`}</StyledTypography>}

        <Box sx={{ display: { xs: 'none', md: 'flex' } }}>
          {menuItems?.map((item) => (
            <StyledButton
              key={item.name}
              active={location.pathname.replace(/^\//, '') === item.route}
              name={item.route}
              capital={capitalLetters.toString()}
              hidden={item.hidden}
              onClick={handleCloseNavMenu}
            >
              {Localize.get(`AppNavBar.${item.name}`)}
            </StyledButton>
          ))}
        </Box>

        <Box sx={{ flexGrow: 0, position: 'absolute', right: '1rem', top: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            {colorMode}
            {!!global?.loginAreaVisibility && (
              <HandleAvatar isLogin={isLogin} header={header} user={contextData?.user} />
            )}
          </Box>
        </Box>
      </Container>

      {/* mob */}

      <Toolbar sx={{ display: { xs: 'flex', md: 'none' } }}>
        {LogoLinkComponent}
        {SYS_NAME !== '' && <StyledTypography>{`${SYS_NAME} System`}</StyledTypography>}

        <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none', justifyContent: 'end' } }}>
          {colorMode}
          <IconButton
            size='large'
            aria-label='account of current user'
            aria-controls='menu-appbar'
            aria-haspopup='true'
            onClick={handleDrawerToggle}
            sx={(theme) => ({
              color: theme.palette.common.white,
              display: { xs: isFraunhofer() ? 'none' : 'flex' }
            })}
          >
            <MenuIcon />
          </IconButton>

          <StyledTypographyButton
            component='button'
            onClick={handleDrawerToggle}
            sx={{ display: { xs: isFraunhofer() ? 'none' : 'flex' } }}
          >
            {Localize.get('AppNavBar.menuMobile')}
          </StyledTypographyButton>
        </Box>

        <Box component='nav' aria-label='mailbox folders'>
          <StyledDrawer variant='temporary' open={mobileOpen} onClose={handleDrawerClose}>
            <Box sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
              <List>
                {menuItems?.map((item) => (
                  <ListItem key={item.name} disablePadding>
                    <ListItemButton divider>
                      <StyledLink href={`/${item.route}`}>
                        <ListItemText
                          primary={
                            menuItems?.capitalLetters
                              ? Localize.get(`AppNavBar.${item.name}`).toUpperCase()
                              : Localize.get(`AppNavBar.${item.name}`)
                          }
                        />
                      </StyledLink>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          </StyledDrawer>
        </Box>
        {!!global?.loginAreaVisibility && (
          <Box>
            <HandleAvatar isLogin={isLogin} header={header} user={contextData?.user} />
          </Box>
        )}
      </Toolbar>
    </StyledAppBar>
  );
};

export default Header;
